import type { ReactNode } from 'react'

import PanelSwitch from '../PanelSwitch'

/** External doc or source link for the About panel (e.g. GitHub, MDN). */
export type ToolAboutLink = {
  name: string
  href: string
}

export type ToolAbout = {
  description: string
  /** Doc or source links (for example MDN or GitHub). */
  libraries: ToolAboutLink[]
}

/** Left-column header pills (INPUT, DIFF, etc.). Matches OUTPUT chip in ToolWorkspace. */
export const TOOL_IO_CHIP_CLASS =
  'bg-tertiary-fixed px-2 py-0.5 font-mono text-[10px] font-bold leading-none tracking-widest text-on-tertiary-fixed'

export function ToolIoChip({ children }: { children: ReactNode }) {
  return <span className={TOOL_IO_CHIP_CLASS}>{children}</span>
}

/** Shared Tailwind for algorithm / codec `<select>` in tool headers. */
export const TOOL_CODEC_SELECT_CLASS =
  'form-well max-w-[min(18rem,52vw)] min-w-0 cursor-pointer py-1 pl-2 pr-7 font-mono text-[11px] text-on-surface sm:max-w-[22rem] sm:text-xs'

export function WrapLinesPanelSwitch({
  lineWrap,
  onLineWrapChange,
}: {
  lineWrap: boolean
  onLineWrapChange: (next: boolean) => void
}) {
  return (
    <PanelSwitch
      label="Wrap long lines"
      description="Hides line numbers in the editor."
      checked={lineWrap}
      onCheckedChange={onLineWrapChange}
    />
  )
}

/** Stable handler for tools that hide the swap control (swapDisabled). */
export function noopToolSwap(): void {}

/** Vertical rule between mode / options stacks in the center rail (lg). */
export const TOOL_CENTER_RAIL_RULE_CLASS =
  'hidden h-12 w-px bg-gradient-to-b from-transparent via-outline-variant/30 to-transparent lg:block'

/** Collapsed-by-default About accordion: description plus linked libraries/APIs. Shared by
 * `ToolWorkspace` (the encode/decode tool shell) and any freeform tool page that doesn't use it. */
export function ToolAboutSection({ about }: { about: ToolAbout }) {
  return (
    <details className="group mt-8 rounded-lg border border-outline-variant/20 bg-surface-container-low open:bg-surface-container-low/80">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 font-mono text-xs font-bold uppercase tracking-widest text-on-surface transition-colors hover:bg-surface-container-high/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-outline/50 [&::-webkit-details-marker]:hidden">
        <span>About</span>
        <span className="material-symbols-outlined text-base text-on-surface-variant transition-transform group-open:rotate-180">
          expand_more
        </span>
      </summary>
      <div className="space-y-4 border-t border-outline-variant/15 px-4 py-4">
        <p className="text-sm leading-relaxed text-on-surface-variant">{about.description}</p>
        <div>
          <h2 className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
            Libraries and APIs
          </h2>
          <ul className="list-inside list-disc space-y-1.5 text-sm text-on-surface">
            {about.libraries.map((lib) => (
              <li key={lib.href}>
                <a
                  href={lib.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-primary-container underline decoration-primary-container/40 underline-offset-2 hover:decoration-primary-container"
                >
                  {lib.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  )
}
